/**
 * Single source of truth for the public (signed-out) site navigation.
 * Used by the Landing header and PublicLayout header, in both their
 * desktop and mobile/compact menus, so the two can never drift apart.
 *
 * Canonical order:
 * Home → Features → How it Works → Academy → Guide → Help Center →
 * Blog → FAQ → About → Demo → Contact
 */
import i18n from "@/lib/i18n";

export interface PublicNavItem {
  to: string;
  labelKey: string;
  /** Computed on read so existing consumers stay reactive after language change. */
  readonly label: string;
  /** react-router `end` matching (only the Home route needs it). */
  end?: boolean;
}

const navItem = (to: string, labelKey: string, end?: boolean): PublicNavItem => ({
  to,
  labelKey,
  end,
  get label() {
    return i18n.t(labelKey);
  },
});

export const PUBLIC_NAV: PublicNavItem[] = [
  navItem("/", "publicChrome.home", true),
  navItem("/features", "publicChrome.features"),
  navItem("/how-it-works", "publicChrome.howItWorks"),
  navItem("/academy", "publicChrome.academy"),
  navItem("/tennis-statistics-guide", "publicChrome.guide"),
  navItem("/help", "publicChrome.helpCenter"),
  navItem("/blog", "publicChrome.blog"),
  navItem("/faq", "publicChrome.faq"),
  navItem("/about", "publicChrome.about"),
  navItem("/demo", "publicChrome.demo"),
  navItem("/contact", "publicChrome.contact"),
];

/** Extra legal links shown only inside the landing mobile menu. */
export const PUBLIC_NAV_LEGAL: PublicNavItem[] = [
  navItem("/privacy", "publicChrome.privacy"),
  navItem("/terms", "publicChrome.terms"),
];

/** Shared class for a public header nav link. */
export const navLinkClass = (isActive: boolean) =>
  `px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
    isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
  }`;

/**
 * Shared sizing for the desktop header CTAs (Download app / Sign in / Open app)
 * so both controls share height, padding, radius and baseline.
 */
export const HEADER_CTA_CLASS =
  "flex items-center !h-11 !px-4 !text-sm !rounded-md [&_span]:!max-w-none [&_span]:!overflow-visible";

